import { useRef, type ReactNode } from "react";
import { generateErrorId } from "#shared/errors/errorId.ts";
import { getErrorDefinition } from "#shared/errors/registry.ts";
import { Button } from "@web/components/ui/button.tsx";
import { useAppStatus, useConnectSpotify } from "@web/features/library/api.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { CopyDetailsButton } from "./CopyDetailsButton.tsx";
import { resolveError } from "./resolveError.ts";

/**
 * Persistent banner for connection-level problems (API down, token expired).
 * Does not toast-and-vanish.
 */
export function ConnectionBanner() {
  const status = useAppStatus({ refetchInterval: 15_000 });
  const connect = useConnectSpotify();
  const debugMode = useUiStore((s) => s.debugMode);
  const expiredIdRef = useRef<{ key: string; id: string } | null>(null);

  if (status.isError) {
    const resolved = resolveError(status.error, "API_UNREACHABLE");
    return (
      <BannerShell tone="error">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{resolved.title}</p>
          <p className="text-sm text-muted">{resolved.message}</p>
          <p className="mt-0.5 font-mono text-xs text-faint">
            {debugMode ? `${resolved.code} · ` : "Ref "}
            {resolved.errorId}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void status.refetch()}
          >
            Retry
          </Button>
          <CopyDetailsButton
            errorId={resolved.errorId}
            code={resolved.code}
            message={resolved.message}
            action="status poll"
          />
        </div>
      </BannerShell>
    );
  }

  const data = status.data;
  if (!data) return null;

  const expired =
    data.connected &&
    !data.api_ok &&
    !!data.error &&
    /expired|reconnect|session/i.test(data.error);

  if (expired) {
    const key = data.error ?? "";
    if (!expiredIdRef.current || expiredIdRef.current.key !== key) {
      expiredIdRef.current = { key, id: generateErrorId() };
    }
    const expiredErrorId = expiredIdRef.current.id;
    const def = getErrorDefinition("SPOTIFY_TOKEN_EXPIRED");
    return (
      <BannerShell tone="warning">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{def.title}</p>
          <p className="text-sm text-muted">{def.message}</p>
          <p className="mt-0.5 font-mono text-xs text-faint">
            {debugMode ? `${def.code} · ` : "Ref "}
            {expiredErrorId}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={connect.isPending}
            onClick={() => connect.mutate()}
          >
            Reconnect
          </Button>
          <CopyDetailsButton
            errorId={expiredErrorId}
            code={def.code}
            message={data.error ?? def.message}
            action="spotify status"
          />
        </div>
      </BannerShell>
    );
  }

  expiredIdRef.current = null;
  return null;
}

function BannerShell({
  tone,
  children,
}: {
  tone: "error" | "warning";
  children: ReactNode;
}) {
  return (
    <div
      className={
        tone === "error"
          ? "flex flex-wrap items-center gap-3 border-b border-status-cancelled/40 bg-status-cancelled/10 px-4 py-2.5"
          : "flex flex-wrap items-center gap-3 border-b border-status-working/40 bg-status-working/10 px-4 py-2.5"
      }
      role="alert"
    >
      {children}
    </div>
  );
}
